/* eslint-disable max-lines */
/* eslint-disable camelcase */
/* eslint-disable react/require-default-props */
import { ActionCreatorWithOptionalPayload } from '@reduxjs/toolkit';
import { fetchFilterCategories } from 'actions';
import ShowLabelsCategories from 'components/Filters/BackendResourceFilter/ShowLabelsCategories';
import LoadingIndicator from 'components/LoadingIndicator';
import useClickOutside from 'components/utils/useClickOutside';
import { EXTERNAL_LINK_WHITE_ICON } from 'constants/commonIcons';
import { SEARCH_ICON } from 'constants/filterIcons';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import { FiltersTreeContext } from 'contexts/ExploreFiltersTreeContext';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import LoadingOverlayWrapper from 'react-loading-overlay-ts';
import { useDispatch } from 'react-redux';
import { getFetchAllCampaignRuleOutcomesLoading } from 'selectors/campaignRuleOutcomes';
import {
  getCategoriesList,
  getModelsIdsFromCategories,
  getRuleGroupIdsFromCategories,
} from 'selectors/categories';
import { getModelsList } from 'selectors/models';
import { getNavParamsByResourceMemo } from 'selectors/nav';
import { getRuleGroupsList } from 'selectors/ruleGroups';
import { getFetchAllRulesLoading } from 'selectors/rules';
import { useSelector } from 'store';
import { MModel, MRuleGroup } from 'types';
import logEvent from 'utils/analytics';
import { triggerURIDownload } from 'utils/triggerDownload';
import { v5 as uuidv5 } from 'uuid';
import CategoriesFilterItem from './CategoriesFilterItem';

type ResourceFilterProps = {
  className?: string;
  title?: string;
  dataTestid?: string;
  campaignsKey: string;
  rulesKey: string;
  isAdvanceFilterView?: boolean;
  isOpening?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchCampaigns: ActionCreatorWithOptionalPayload<any, string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchRules: ActionCreatorWithOptionalPayload<any, string>;
  treeContext: React.Context<FiltersTreeContext>;
};

const CategoriesFilter: React.FC<ResourceFilterProps> = (props) => {
  const {
    className,
    title = '',
    dataTestid,
    campaignsKey,
    rulesKey,
    isAdvanceFilterView,
    isOpening,
    fetchCampaigns,
    fetchRules,
    treeContext,
  } = props;

  const campaignsKeys = campaignsKey;
  const rulesKeys = rulesKey;

  const dispatch = useDispatch();

  const { tree, treeParams, filtersSearchString, handleAddValueToTree, handleRemoveValueFromTree } =
    useContext(treeContext);

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  const allCampaignsWithCounts = useSelector(getModelsList);
  const allRulesWithCounts = useSelector(getRuleGroupsList);

  const envelopeParams = useSelector((state) =>
    getNavParamsByResourceMemo(state, resourceQueryParamName.envelopes)
  );

  const modelsUuids = useSelector(getModelsIdsFromCategories);
  const rulesUuids = useSelector(getRuleGroupIdsFromCategories);

  const categories = useSelector(getCategoriesList);

  const loadingRules = useSelector(getFetchAllRulesLoading);
  const loadingCampaignRuleOutcomes = useSelector(getFetchAllCampaignRuleOutcomesLoading);
  const loading = loadingRules || loadingCampaignRuleOutcomes;

  const filterCampaigns = (treeParams[campaignsKeys] ?? []) as string[];
  const notFilterCampaigns = (treeParams[`not_${campaignsKeys}`] ?? []) as string[];
  const filterRules = (treeParams[rulesKeys] ?? []) as string[];
  const notFilterRules = (treeParams[`not_${rulesKeys}`] ?? []) as string[];

  // get models from categories TODO decorate with counts
  let allCampaigns = Array.from(
    new Set(
      categories
        .map((c) => {
          // @ts-ignore

          if (c.tenant_name === 'human') {
            return { ...c.model, sourceUuid: c.model_uuid, tenantName: 'human' };
          }
          return {
            ...c.model,
            name: `${c.model?.name} (Staging)`,
            uuid: uuidv5('modeler', c.model_uuid || ''),
            sourceUuid: c.model_uuid,
            tenantName: 'modeler',
          };
        })
        .map((c) => JSON.stringify(c))
    )
  ).map((c) => JSON.parse(c));

  allCampaigns = allCampaigns.map((c) => ({
    ...c,
    // @ts-ignore
    count: allCampaignsWithCounts.find((m) => m.uuid === c.uuid)?.count || '-',
  }));

  let allRules = Array.from(
    new Set(
      categories
        .map((c) => {
          // @ts-ignore
          if (c.tenant_name === 'human') {
            return {
              ...c.rule_head?.rule_group,
              sourceUuid: c.rule?.rule_group_uuid,
              tenantName: 'human',
            };
          }
          return {
            ...c.rule_head?.rule_group,
            name: `${c.rule_head?.rule_group?.name} (Staging)`,
            uuid: uuidv5('modeler', c.rule?.rule_group_uuid || ''),
            sourceUuid: c.rule?.rule_group_uuid,
            tenantName: 'modeler',
          };
        })
        .map((r) => JSON.stringify(r))
    )
  ).map((r) => JSON.parse(r));

  allRules = allRules.map((r) => ({
    ...r,
    // @ts-ignore
    count: allRulesWithCounts.find((m) => m.uuid === r.uuid)?.count || '-',
  }));

  if (isAdvanceFilterView) {
    allRules = [];
  }

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);
  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, handleClickOutside);

  useEffect(() => {
    if (isOpen) {
      dispatch(
        fetchCampaigns({
          uuids: modelsUuids,
          limit: '-1',
          filtersSearchString,
        })
      );
      dispatch(
        fetchRules({
          uuids: rulesUuids,
          limit: '-1',
          filtersSearchString,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    isOpen,
    envelopeParams.filters_search,
    modelsUuids,
    rulesUuids,
    filtersSearchString,
  ]);

  useEffect(() => {
    dispatch(fetchFilterCategories);
  }, [dispatch]);

  useEffect(() => {
    if (isOpening) {
      inputRef.current?.focus();
    }
  }, [inputRef, isOpening]);

  const handleButtonClick = (): void => {
    if (isOpen) return;
    setIsOpen(!isOpen);
  };

  const handleDelete = (labelId: string, exclude: boolean): void => {
    if (exclude) {
      if (notFilterRules.includes(labelId)) {
        handleRemoveValueFromTree(labelId, `not_${rulesKeys.replace('_and', '')}`);
      } else {
        handleRemoveValueFromTree(labelId, `not_${campaignsKeys.replace('_and', '')}`);
      }
    } else if (filterRules.includes(labelId)) {
      handleRemoveValueFromTree(labelId, rulesKeys.replace('_and', ''));
    } else {
      handleRemoveValueFromTree(labelId, campaignsKeys.replace('_and', ''));
    }
  };

  const handleInputChange = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    exclude: boolean,
    state: string,
    checked: string | undefined,
    isRule: boolean
  ): void => {
    logEvent('envelopes-list-filter-models');

    if (
      exclude &&
      ((isRule && filterRules.includes(state)) || (!isRule && filterCampaigns.includes(state)))
    ) {
      return;
    }

    const item =
      allCampaigns.find((c) => c.uuid === state) || allRules.find((r) => r.uuid === state);

    if (checked === 'true') {
      let key = '';
      if (isRule) {
        key = rulesKeys;
      } else {
        key = campaignsKeys;
      }

      if (exclude) {
        handleAddValueToTree(state, `not_${key}`.replace('and_', ''), item?.name || '');
      } else {
        handleAddValueToTree(state, key.replace('_and', ''), item?.name || '');
      }
    } else {
      handleDelete(state, notFilterRules.includes(state) || notFilterCampaigns.includes(state));
    }

    event.stopPropagation();
  };

  const getRuleData = (): (MRuleGroup & {
    render: boolean;
    sourceUuid?: string;
    tenantName?: string;
  })[] =>
    allRules.map((r) =>
      r.name && r.name.toLowerCase().includes(searchValue.toLowerCase())
        ? { ...r, render: true }
        : { ...r, render: false }
    ) || [];

  const ruleMatchesCategory = (
    r: MRuleGroup & { sourceUuid?: string; rule_uuid?: string; tenantName?: string },
    category: typeof categories[number] & { tenant_name?: string },
    modelSourceUuid: string,
    modelTenantName?: string
  ): boolean => {
    if (modelTenantName && category.tenant_name !== modelTenantName) {
      return false;
    }
    if (r.tenantName && category.tenant_name !== r.tenantName) {
      return false;
    }

    const ruleGroupUuid = category.rule?.rule_group_uuid;
    const matchesRule =
      ruleGroupUuid === r.uuid ||
      ruleGroupUuid === r.sourceUuid ||
      // @ts-ignore
      ruleGroupUuid === r.rule_uuid;
    return !!matchesRule && category.model_uuid === modelSourceUuid;
  };

  const getCampaignData = (): (MModel & { sourceUuid?: string; tenantName?: string })[] =>
    allCampaigns.filter(({ name, uuid, sourceUuid, tenantName }) => {
      if (name && name.toLowerCase().includes(searchValue.toLowerCase())) {
        return true;
      }

      const rules = getRuleData().filter((r) =>
        categories.some((category) =>
          ruleMatchesCategory(r, category, sourceUuid || uuid, tenantName)
        )
      );

      if (rules.length > 0) return true;

      return false;
    }) || [];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e?.target.value.toLowerCase();
    setSearchValue(value);
  };

  const handleExportCounts = (): void => {
    const campaigns = getCampaignData();
    const rules = getRuleData();

    const campaignsData = campaigns.map((c) => ({
      name: c.name,
      // @ts-ignore
      count: !c.count ? 0 : c.count,
    }));
    const rulesData = rules.map((r) => ({
      name: r.name,
      // @ts-ignore
      count: !r.count ? 0 : r.count,
    }));

    const data = [...campaignsData, ...rulesData];
    const stringData = data.reduce((acc, val) => `${acc}${val.name},${val.count}\n`, '');
    const formattedData = `data:text/csv;charset=utf-8,Name,Count\n${stringData}`;

    triggerURIDownload(formattedData, title);
  };

  return (
    <>
      <div ref={wrapperRef} className={`flex flex-col ${className || ''}"`}>
        <div className="relative flex flex-row justify-start items-center w-full min-h-8 rounded p-1.5 bg-litlingo-success-light bg-opacity-10">
          <div className="w-5">{SEARCH_ICON('white')}</div>
          <input
            id="search"
            name="search"
            ref={inputRef}
            className="max-h-5 w-full text-white ml-1 font-normal text-base bg-transparent placeholder-white placeholder-italic"
            placeholder={`Search ${title}`}
            data-testid={dataTestid}
            type="text"
            onFocus={handleButtonClick}
            onChange={handleSearchChange}
            value={searchValue}
            autoComplete="off"
          />
        </div>

        {isOpen && (
          <>
            <LoadingOverlayWrapper
              active={loading && getCampaignData().length === 0 && getRuleData().length === 0}
              spinner={<LoadingIndicator />}
              fadeSpeed={0}
            >
              <div className="w-full max-h-filter z-10 overflow-auto bg-litlingo-gray-1 custom-scrollbar">
                <fieldset className="min-h-5 flex flex-col">
                  {getCampaignData().map((item) => {
                    const rules = getRuleData().filter((r) =>
                      categories.some((category) =>
                        ruleMatchesCategory(
                          r,
                          category,
                          item.sourceUuid || item.uuid,
                          item.tenantName
                        )
                      )
                    );

                    return (
                      <CategoriesFilterItem
                        key={item.uuid}
                        campaign={item}
                        rules={rules}
                        filterCampaigns={filterCampaigns}
                        notFilterCampaigns={notFilterCampaigns}
                        filterRules={filterRules}
                        notFilterRules={notFilterRules}
                        handleInputChange={handleInputChange}
                      />
                    );
                  })}
                  {/* {getRuleData()
                    .filter((r) => {
                      if (!outcomes.find((outcome) => outcome.rule_uuid === r.uuid)) {
                        return true;
                      }

                      if (
                        outcomes.find(
                          (outcome) =>
                            outcome.rule_uuid === r.uuid &&
                            allCampaigns.every((c) => outcome.campaign?.name !== c.name) &&
                            !outcome.campaign?.deleted_at
                        )
                      ) {
                        return true;
                      }
                      return false;
                    })
                    .map((item) => (
                      <CategoriesRuleFilterItem
                        key={item.uuid}
                        rule={item}
                        filterRules={filterRules}
                        notFilterRules={notFilterRules}
                        handleInputChange={handleInputChange}
                      />
                    ))} */}
                </fieldset>
              </div>
            </LoadingOverlayWrapper>

            <div className="mt-2 -mb-2 flex flex-row justify-between items-center">
              <button type="button" className="focus:outline-none" onClick={handleExportCounts}>
                {EXTERNAL_LINK_WHITE_ICON}
              </button>
              <span className="text-white font-normal text-xss">Shift + click to exclude</span>
            </div>
          </>
        )}

        {!isOpen && (
          <>
            <ShowLabelsCategories
              handleDelete={handleDelete}
              campaignsKey={campaignsKey}
              rulesKey={rulesKey}
              filterCampaigns={filterCampaigns}
              notFilterCampaigns={notFilterCampaigns}
              filterRules={filterRules}
              notFilterRules={notFilterRules}
              categories={categories}
              tree={tree}
            />
          </>
        )}
      </div>
    </>
  );
};

export default CategoriesFilter;
